import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react'

import { useUnit } from 'effector-react'
import debounce from 'lodash/debounce'
import uniqBy from 'lodash/uniqBy'
import type { InfiniteData } from 'react-query'
import { useInfiniteQuery, useQueryClient } from 'react-query'

import { $creatorId } from '@/models/creators.ts'
import { listsService } from '@/modules/chatting/service/lists'
import type {
  FollowingUsersFilters,
  GetListUsersResponse,
  GetUsersListFilters,
  ListUser,
} from '@/modules/chatting/types'
import { handleError } from '@/utils/error'
import { isTruthyAndNotEmpty } from '@/utils/isTruthy'

import {
  listUsersOrderOptions,
  listUsersSortOptions,
} from './CollectionInfo/consts'
import type { CollectionsDialogConfig } from './CollectionsDialog'
import type { CollectionsItem } from './CollectionsList/types'

interface Props {
  collection?: CollectionsItem
  config: CollectionsDialogConfig
}

const defaultFilters = {} as FollowingUsersFilters | GetUsersListFilters

export const useComputeCollectionContext = ({ collection, config }: Props) => {
  const [isRequestEnabled, setIsRequestEnabled] = useState(!!collection)
  const creatorId = useUnit($creatorId)
  const [filters, setFilters] = useState(defaultFilters)

  useEffect(
    () => {
      setIsRequestEnabled(!!collection)
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [collection?.id],
  )

  useEffect(() => {
    if (config.filters && config.listId === collection?.id) {
      setFilters(config.filters)
    } else {
      setFilters(defaultFilters)
    }
  }, [config, collection?.id])

  const orderOptions = useMemo(() => {
    if (!collection || collection.orderOptions === null) return []

    return collection.orderOptions ?? listUsersOrderOptions
  }, [collection])
  const defaultOrderValue = useMemo(
    () => orderOptions[0]?.value,
    [orderOptions],
  )

  const [query, setQuery] = useState('')
  const [orderValue, setOrderValue] = useState(defaultOrderValue)

  const order = useMemo(
    () => orderValue ?? defaultOrderValue,
    [defaultOrderValue, orderValue],
  )

  const isDefaultOrderUnsortable = useMemo(
    () =>
      collection?.type === 'custom' ||
      collection?.id === 'restricted' ||
      collection?.id === 'blocked',
    [collection],
  )

  const sortOptions = useMemo(() => {
    if (isDefaultOrderUnsortable && order === 'default') {
      return listUsersSortOptions.map((option) => ({
        ...option,
        disabled: true,
      }))
    }

    return listUsersSortOptions
  }, [order, isDefaultOrderUnsortable])

  const [sort, setSort] = useState(sortOptions[1].value)

  useEffect(() => {
    setOrderValue(defaultOrderValue)
  }, [defaultOrderValue])

  const resetCollectionUsersRequest = useCallback(() => {
    setIsRequestEnabled(false)
    setQuery('')
    setSort(sortOptions[1].value)
    setOrderValue(defaultOrderValue)
  }, [defaultOrderValue, sortOptions])

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const handleSetQuery = useCallback(debounce(setQuery, 500), [])

  const queryClient = useQueryClient()

  const usersQueryKey = useMemo(
    () => [
      'listUsers',
      creatorId,
      collection?.id,
      sort,
      order,
      query,
      JSON.stringify(filters),
    ],
    [creatorId, collection?.id, sort, order, query, filters],
  )

  const {
    data,
    isLoading: isLoadingBase,
    isIdle,
    isFetchingNextPage,
    hasNextPage,
    fetchNextPage,
    isError,
    refetch,
    isFetching,
  } = useInfiniteQuery(
    usersQueryKey,
    ({ pageParam: { offset = 0, pinned = true } = {} }) => {
      if (collection?.usersFetcher) {
        if (collection?.id === 'restricted') {
          return collection.usersFetcher({
            creatorId: creatorId!,
            query,
            offset,
          })
        }

        if (collection?.id === 'blocked') {
          return collection.usersFetcher({
            creatorId: creatorId!,
            query,
            sort,
            offset,
            order,
          })
        }

        if (
          collection?.id === 'fans-all' ||
          collection?.id === 'fans-active' ||
          collection?.id === 'fans-expired'
        ) {
          return collection.usersFetcher({
            creatorId: creatorId!,
            query,
            offset,
            filters: filters as GetUsersListFilters,
          })
        }

        if (
          collection?.id === 'following-all' ||
          collection?.id === 'following-active' ||
          collection?.id === 'following-expired'
        ) {
          return collection.usersFetcher({
            creatorId: creatorId!,
            query,
            sort,
            offset,
            order,
            filters: filters as FollowingUsersFilters,
          })
        }
      }

      return listsService.getUsers({
        creatorId: creatorId!,
        listId: collection!.id,
        query,
        sort,
        offset,
        pinned,
        order,
      })
    },
    {
      enabled: isRequestEnabled && !!collection?.id,
      getNextPageParam: (lastPage, allPages) => {
        const allUsersCount = allPages.reduce(
          (acc, page) => acc + page.list.length,
          0,
        )
        const isNotPinnedRemaining =
          lastPage.pinned &&
          !lastPage.hasMore &&
          (collection?.usersCount ?? 0) > allUsersCount

        const pinnedPagesCount = allPages.filter((page) => page.pinned).length
        const unpinnedPagesCount = allPages.length - pinnedPagesCount

        let offset =
          (lastPage.pinned && isNotPinnedRemaining) || !lastPage.pinned
            ? unpinnedPagesCount * 10
            : pinnedPagesCount * 10

        if (lastPage.nextOffset && lastPage.hasMore) {
          offset = lastPage.nextOffset
        }

        if (lastPage.hasMore || isNotPinnedRemaining) {
          return {
            offset,
            pinned: lastPage.pinned && lastPage.hasMore,
          }
        }
      },
      onError: (error) => {
        handleError(error, 'Something went wrong while getting users')
      },
    },
  )

  const users = useMemo(() => {
    const allUsers =
      data?.pages.flatMap((page) => page.list).filter(isTruthyAndNotEmpty) ?? []
    const { pinned: pinnedUsers, unpinned: unpinnedUsers } = allUsers.reduce<{
      pinned: ListUser[]
      unpinned: ListUser[]
    }>(
      (acc, user) => {
        if (user.isPinned) {
          acc.pinned.push(user)
        } else {
          acc.unpinned.push(user)
        }

        return acc
      },
      { pinned: [], unpinned: [] },
    )

    if (query) {
      return unpinnedUsers.map((user) => ({
        ...user,
        isPinned: pinnedUsers?.some((pinnedUser) => pinnedUser.id === user.id),
      }))
    } else {
      return uniqBy([...pinnedUsers, ...unpinnedUsers], 'id')
    }
  }, [data, query])

  const handleSetOrder = useCallback(
    (order: (typeof orderOptions)[number]['value']) => {
      if (isDefaultOrderUnsortable && order === 'default' && sort === 'asc') {
        setSort('desc')
      }
      setOrderValue(order)
    },
    [sort, isDefaultOrderUnsortable],
  )

  const updateUserInCache = useCallback(
    (updatedUser: ListUser) => {
      queryClient.setQueriesData<
        InfiniteData<GetListUsersResponse> | undefined
      >(['listUsers', creatorId], (data) => {
        if (!data) {
          return data
        }

        return {
          ...data,
          pages: data.pages.map((page) => ({
            ...page,
            list: page.list.map((user) =>
              user.id === updatedUser.id ? updatedUser : user,
            ),
          })),
        }
      })
    },
    [queryClient, creatorId],
  )

  return useMemo(
    () => ({
      users,
      isLoading:
        isLoadingBase || (users.length === 0 && isFetchingNextPage) || isIdle,
      isFetchingNextPage,
      hasNextPage,
      fetchNextPage,
      query,
      setQuery: handleSetQuery,
      sort,
      setSort,
      order,
      setOrder: handleSetOrder,
      usersQueryKey,
      updateUserInCache,
      orderOptions,
      sortOptions,
      collection,
      resetCollectionUsersRequest,
      filters,
      setFilters,
      isError,
      refetch,
      isFetching,
    }),
    [
      users,
      isLoadingBase,
      isFetchingNextPage,
      hasNextPage,
      fetchNextPage,
      query,
      handleSetQuery,
      sort,
      setSort,
      order,
      handleSetOrder,
      usersQueryKey,
      updateUserInCache,
      orderOptions,
      sortOptions,
      collection,
      resetCollectionUsersRequest,
      filters,
      setFilters,
      isIdle,
      isError,
      refetch,
      isFetching,
    ],
  )
}

export const CollectionContext = createContext<ReturnType<
  typeof useComputeCollectionContext
> | null>(null)

export const useCollectionContext = () => {
  const context = useContext(CollectionContext)

  if (!context) {
    throw new Error(
      'useCollectionContext must be used within a CollectionProvider',
    )
  }

  return context
}
